<template>
  <div :class="classes()">
    <Headline noMargin size="m" isViewHeadline>Pflegeprofil</Headline>
    <IntroBanner
      :headline="$t('careProfile.introBanner.headline')"
      :copy="$t('careProfile.introBanner.copy')"
      :bottomSpace="40"
      :localStorage="LS_CAREPROFILE_INFO_BANNER"
    />

    <Copy
      v-if="error"
      size="m"
      color="supportDanger"
      centered
      bold
      responsive
      staticClass="u-margin__bottom--4"
    >
      {{ $t('profile.forms.error.generics') }}
    </Copy>

    <Widget ref="widget" hasHeadlineDivider>
      <Headline slot="headline" :staticClass="`${el}__headline`" noMargin :headine="3" size="xs">
        {{ $t('careProfile.form.label.personalData') }}
        <Icon
          name="information-line"
          color="primaryBlue"
          :content="$t('careProfile.form.info.personalData')"
          v-tippy="{ arrow: true }"
          isButton
          @mouseenter="() => trackClick('-PD_content-InfoIconShown')"
          :onClick="() => trackClick('-PD_content-InfoIconShown')"
        />
      </Headline>

      <CareProfileForm
        v-if="editMode"
        :profileData="profileData"
        @submit="changeEditMode"
        @close="changeEditMode"
        @dataChanged="changeData"
        @onTrackingClick="trackClick"
      />

      <div v-else>
        <div v-if="isLoading" :class="`${el}__spinner`">
          <Spinner />
        </div>

        <div v-else>
          <div :class="`${el}__initialsCircle ${el}__initialsCircleText`">
            {{ profileInitials }}
            <Icon
              v-if="profileInitials === ''"
              name="folder-user-line"
              color="primaryBlue"
              size="xxl"
            />
          </div>

          <Divider :class="`${el}__divider`" class="u-margin__vertical--3" />

          <Row class="u-padding__vertical--1">
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.firstname') + ':' }}
                </Copy>
                <Copy element="div" size="l" bold>
                  {{ profileData.firstname || '-' }}
                </Copy>
              </div>
            </Col>
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.lastname') + ':' }}
                </Copy>

                <Copy element="div" size="l" bold>
                  {{ profileData.lastname || '-' }}
                </Copy>
              </div>
            </Col>
          </Row>

          <Row class="u-padding__vertical--1">
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.gender') + ':' }}
                </Copy>
                <Copy element="div" size="l" bold>
                  {{
                    profileData.gender
                      ? $t('careProfile.form.value.gender')[profileData.gender].label
                      : '-'
                  }}
                </Copy>
              </div>
            </Col>
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.birthdate') + ':' }}
                </Copy>

                <Copy element="div" size="l" bold>
                  {{ profileData.birthdate ? formatDate(profileData.birthdate) : '-' }}
                </Copy>
              </div>
            </Col>
          </Row>

          <Row class="u-padding__vertical--2">
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.street') + ':' }}
                </Copy>
                <Copy element="div" size="l" bold>
                  {{
                    profileData.street ? profileData.street + ' ' + profileData.houseNumber : '-'
                  }}
                </Copy>
              </div>
            </Col>
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.city') + ':' }}
                </Copy>

                <Copy element="div" size="l" bold>
                  {{
                    profileData.city || profileData.zipCode
                      ? profileData.zipCode + ' ' + profileData.city
                      : '-'
                  }}
                </Copy>
              </div>
            </Col>
          </Row>

          <Row>
            <Col :xs="12" :m="4" :mOffset="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.typeOfResidency') + ':' }}
                </Copy>
                <Copy element="div" size="l" bold>
                  {{
                    profileData.typeOfResidency
                      ? $t('careProfile.form.value.typeOfResidency')[profileData.typeOfResidency]
                          .label
                      : '-'
                  }}
                </Copy>
              </div>
            </Col>
          </Row>

          <Row class="u-padding__top--2">
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.careFund') + ':' }}
                </Copy>

                <Copy element="div" size="l" bold>
                  <strong>{{ profileData.careFund ? profileData.careFund.name : '-' }}</strong>
                </Copy>
              </div>
            </Col>
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.insuranceNumber') + ':' }}
                </Copy>
                <Copy element="div" size="l" bold>
                  <strong>{{ profileData.insuranceNumber || '-' }}</strong>
                </Copy>
              </div>
            </Col>
          </Row>

          <Row class="u-padding__top--2">
            <Col :xs="12" :m="4">
              <div>
                <Copy element="div" size="s" color="tertiaryLight">
                  {{ $t('careProfile.form.label.profileCreatedAt') + ':' }}
                </Copy>
                <Copy element="div" size="l" bold>
                  {{ profileData.createdAt }}
                </Copy>
              </div>
            </Col>
          </Row>

          <Row class="u-padding__top--3">
            <Col :xs="6">
              <Link
                icon="pencil-line"
                noUnderline
                @click="changeEditMode"
                :class="`${el}__editButton`"
              >
                {{ $t('careProfile.form.button.edit') }}
              </Link>
            </Col>
          </Row>
        </div>
      </div>
    </Widget>

    <CareLevelAnalysisWidget @onTrackingClick="trackClick" />
  </div>
</template>

<script>
import IntroBanner from '@/components/IntroBanner';
import CareProfileForm from '@/components/Forms/CareProfileForm';
import Headline from '@/objects/Headline';
import { LS_CAREPROFILE_INFO_BANNER } from '@/constants/storage';
import Divider from '@/objects/Divider';
import Link from '@/objects/Link';
import Icon from '@/objects/Icon';
import Col from '@/components/Grid/Col';
import Copy from '@/objects/Copy';
import Row from '@/components/Grid/Row';
import Widget from '@/objects/Widget';
import CareLevelAnalysisWidget from '@/views/CareLevelCalculator/Analysis/AnalysisWidget';

import Vue from 'vue';

import { ROUTE_API_GET_CARE_PROFILE_DATA } from '@/constants/apiRoutes';
import { formatDate } from '@/helpers/formatter';
import { scrollTo } from '@/helpers/scroll';

import initialsMixin from '@/mixins/initials';
import Spinner from '@/objects/Spinner/Spinner';
import { createTrackingData } from '@/helpers/tracking/utils/helpers';

export default {
  name: 'CareProfiles',
  components: {
    Spinner,
    Row,
    Copy,
    Icon,
    Col,
    Headline,
    CareProfileForm,
    IntroBanner,
    Divider,
    Link,
    Widget,
    CareLevelAnalysisWidget,
  },
  props: {},
  mixins: [initialsMixin],

  created() {
    this.getCareProfileData();
  },

  beforeRouteLeave(to, from, next) {
    if (this.dataChanged) {
      next(false);
      this.alert(to);
    } else {
      setInterval(() => {
        next();
      }, 10);
    }
  },

  data() {
    return {
      LS_CAREPROFILE_INFO_BANNER,
      error: false,
      isLoading: true,
      dataChanged: false,
      editMode: false,
      formatDate,
      profileInitials: '',
      profileData: {
        isProfileOwner: '',
        firstname: '',
        lastname: '',
        birthdate: null,
        gender: null,
        street: '',
        houseNumber: '',
        zipCode: '',
        city: '',
        typeOfResidency: null,
        insuranceNumber: '',
        careFund: null,
        createdAt: null,
      },
      el: 'v-careProfiles',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  methods: {
    getCareProfileData() {
      Vue.axios
        .post(ROUTE_API_GET_CARE_PROFILE_DATA)
        .then((response) => {
          this.setValues(response.data.careProfile);
        })
        .catch(() => {
          this.error = true;
        });
    },

    setValues(object) {
      this.profileData.firstname = object.firstname || '';
      this.profileData.lastname = object.lastname || '';
      this.profileData.street = object.street || '';
      this.profileData.city = object.city || '';
      this.profileData.insuranceNumber = object.insuranceNumber || '';
      this.profileData.careFund = object.careFund || null;
      this.profileData.typeOfResidency = object.typeOfResidency || 0;
      this.profileData.zipCode = object.zipCode || '';
      this.profileData.houseNumber = object.houseNumber || '';
      this.profileData.createdAt = object.createdAt ? this.formatDate(object.createdAt) : '';
      this.profileData.birthdate = object.birthdate || '';
      this.isLoading = false;

      if (object.isProfileOwner) {
        this.profileData.isProfileOwner = object.isProfileOwner;
        this.profileInitials = this.initials;
        this.profileData.gender = this.profileData.isProfileOwner
          ? this.user.gender
          : object.gender;
      } else {
        this.profileData.isProfileOwner = false;
        this.profileInitials = this.getInitials(this.profileData);
        this.profileData.gender = object.gender || 0;
      }
    },

    changeEditMode() {
      this.editMode = !this.editMode;

      if (!this.$matchMedia.l) {
        scrollTo(this.$refs.widget.$el, -56);
      }

      if (this.editMode) {
        this.trackClick('-PD_link-' + this.$t('careProfile.form.button.edit'), 'link');
      }
    },

    changeData(value) {
      this.dataChanged = value;

      if (!this.dataChanged) {
        this.getCareProfileData();
      }
    },

    alert(to) {
      if (this.dataChanged) {
        const confirm = window.confirm(this.$t('careProfile.form.destroy.warning'));
        if (confirm) {
          this.dataChanged = false;
          this.$router.push(to);
        }
      }
    },

    trackClick(labelText, element = 'element') {
      const product = 'Pflegeprofil';
      const label = product + labelText;
      createTrackingData(product, label, element);
    },
  },
};
</script>
